import { safeJsonParse } from '@utils/safeJsonParse'
import { getParsedStorageItem, setStorageItem, STORAGE_EVENT } from '@utils/storageHelper'
import axios from 'axios'
import Cookies from 'js-cookie'
import { type AnalyticsEvents, type BaseAnalyticsEvent } from '../types/events'
import { aletheiaEndpointConfigs } from './aletheiaEventSets'

export const insertOmnichatEasychatBubble = (appKey: string | undefined) => {
  const a = document.createElement('a')

  a.href = 'javascript:;'
  a.id = 'easychat-floating-button'
  a.style.display = 'none'

  const span = document.createElement('span')
  span.id = 'easychat-unread-badge'
  span.style.display = 'none'

  const d1 = document.createElement('div')
  d1.id = 'easychat-close-btn'
  d1.className = 'easychat-close-btn-close'

  const d2 = document.createElement('div')
  d2.id = 'easychat-chat-dialog'
  d2.className = 'easychat-chat-dialog-close'

  const ifrm = document.createElement('iframe')
  ifrm.id = 'easychat-chat-dialog-iframe'
  ifrm.src = `https://client-chat.easychat.co/?appkey=${appKey}&lang=zh-Hant`
  ifrm.style.width = '100%'
  ifrm.style.height = '100%'
  ifrm.setAttribute('frameborder', '0')
  ifrm.setAttribute('scrolling', 'on')

  d2.appendChild(ifrm)

  if (!document.getElementById('easychat-floating-button')) {
    document.body.appendChild(a)
    document.body.appendChild(span)
    document.body.appendChild(d1)
    document.body.appendChild(d2)
  }
}

const GOOGLE_TAG_MANAGER_HOST = 'www.googletagmanager.com'
const GOOGLE_HOSTED_GTM_SCRIPT_URL = `https://${GOOGLE_TAG_MANAGER_HOST}/gtm.js`
const GOOGLE_HOSTED_GTAG_SCRIPT_URL = `https://${GOOGLE_TAG_MANAGER_HOST}/gtag/js`

/**
 * Google Tag Gateway 衡量路徑（官方文件亦稱 tag serving path），key 為 GTM container id 或 GA4 measurement id。
 * 用 map 而非單一字串，是因為一個 gateway 路徑只能綁一個 tag，
 * 未列入的 tag 仍走 googletagmanager.com。
 */
export type TagServingPathConfig = Record<string, string>

export const buildTagServingPathConfig = (
  tagId: string | undefined,
  servingPath: string | undefined,
): TagServingPathConfig => (tagId && servingPath ? { [tagId]: servingPath } : {})

/*
 * 只收單純的路徑片段。這個值會被插進 inline snippet 的單引號字串裡，
 * 所以引號或反斜線會直接毀掉整段 snippet；完整網址（Tag Gateway 後台顯示的形式）
 * 會被組成 `/https://…/` 這種必然 404 的路徑；`..` 這種 relative segment 則會被瀏覽器
 * 解析到 gateway 以外的位置。這些都當設定錯誤處理，回空字串退回官方網址 ——
 * 追蹤退化成第三方載入，總比整站 tag 不載入好。
 */
const SERVING_PATH_SEGMENT_PATTERN = /^[A-Za-z0-9._~-]+$/
const RELATIVE_PATH_SEGMENTS = ['.', '..']

const isValidServingPathSegment = (segment: string): boolean =>
  SERVING_PATH_SEGMENT_PATTERN.test(segment) && !RELATIVE_PATH_SEGMENTS.includes(segment)

/**
 * 衡量路徑正規化為 `/path/` 形式。
 * 前後斜線是 Google 設定指南的硬性要求，缺一邊同源轉發不會生效；
 * 空字串代表該 tag 不走 gateway。
 */
export const normalizeTagServingPath = (servingPath: string | undefined): string => {
  const trimmedServingPath = servingPath?.trim().replace(/^\/+|\/+$/g, '')
  if (!trimmedServingPath) return ''
  if (!trimmedServingPath.split('/').every(isValidServingPathSegment)) {
    console.error('[GA4] servingPath is not a plain path, falling back to googletagmanager.com:', servingPath)
    return ''
  }
  return `/${trimmedServingPath}/`
}

const getScriptUrlPrefix = (googleHostedUrl: string, servingPath?: string): string =>
  normalizeTagServingPath(servingPath) || googleHostedUrl

export const getGtmScriptSrc = (containerId: string, servingPath?: string): string =>
  `${getScriptUrlPrefix(GOOGLE_HOSTED_GTM_SCRIPT_URL, servingPath)}?id=${containerId}`

export const getGtagScriptSrc = (measurementId: string, servingPath?: string): string =>
  `${getScriptUrlPrefix(GOOGLE_HOSTED_GTAG_SCRIPT_URL, servingPath)}?id=${measurementId}`

const insertTagScriptElement = (scriptSrc: string) => {
  const firstScript = document.getElementsByTagName('script')[0]
  const script = document.createElement('script')
  script.async = true
  script.setAttribute('src', scriptSrc)
  firstScript?.parentNode?.insertBefore(script, firstScript)
}

/*
 * gateway 路徑由 CDN 提供、不在這個 repo 裡；設定掉了 snippet 只會拿到 404，
 * tag 靜默不初始化而且沒有任何錯誤訊號。官方 snippet 不給我們 script 元素的 handle，
 * 所以插完再從 DOM 撈回來掛 onerror。
 * 用 error 事件而非逾時探針，是因為逾時在慢速網路會誤判並重複載入同一個容器。
 * 補救時只補 script 元素，不重跑 GTM 的 bootstrap snippet —— 那段會再 push 一次
 * `gtm.start` / `event:'gtm.js'`，而 dataLayer 與初始化事件第一次就已經備妥。
 */
const attachGatewayFallback = (gatewayScriptSrc: string, googleHostedScriptSrc: string) => {
  if (gatewayScriptSrc === googleHostedScriptSrc) return
  const gatewayScript = document.querySelector(`script[src="${gatewayScriptSrc}"]`)
  if (!gatewayScript) return
  gatewayScript.addEventListener('error', () => {
    console.error(`[GA4] ${gatewayScriptSrc} 載入失敗，改用 ${googleHostedScriptSrc}`)
    /* 載入失敗的 script 留在 DOM 裡，會讓後續的 hasGtmScript 把它當成「已載入」 */
    gatewayScript.remove()
    insertTagScriptElement(googleHostedScriptSrc)
  }, { once: true })
}

export const insertGtmScript = (containerId: string, servingPath?: string) => {
  const firstScript = document.getElementsByTagName('script')[0]
  const script = document.createElement('script')
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    '${getScriptUrlPrefix(GOOGLE_HOSTED_GTM_SCRIPT_URL, servingPath)}?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');`
  firstScript?.parentNode?.insertBefore(script, firstScript)
  attachGatewayFallback(getGtmScriptSrc(containerId, servingPath), getGtmScriptSrc(containerId))
}

export const insertGtagScript = (measurementId: string, servingPath?: string) => {
  insertTagScriptElement(getGtagScriptSrc(measurementId, servingPath))
  attachGatewayFallback(getGtagScriptSrc(measurementId, servingPath), getGtagScriptSrc(measurementId))
}

/*
 * 比對解析後的 URL 而非字串相等：官方 snippet 會帶 `&l=`、`gtm_auth`、`gtm_preview` 等參數，
 * 也可能是 protocol-relative 形式，字串比對一律認不出來，於是重複插入同一個 tag。
 */
const findLoadedTagScript = (
  tagId: string,
  googleHostedPathname: string,
  servingPath?: string,
): Element | undefined => {
  const normalizedServingPath = normalizeTagServingPath(servingPath)
  return Array.from(document.querySelectorAll('script[src]')).find((script) => {
    let scriptUrl: URL
    try {
      scriptUrl = new URL(script.getAttribute('src') ?? '', document.baseURI)
    }
    catch {
      return false
    }
    if (scriptUrl.searchParams.get('id') !== tagId) return false
    const isGoogleHosted = scriptUrl.host === GOOGLE_TAG_MANAGER_HOST && scriptUrl.pathname === googleHostedPathname
    const isSameOriginGateway = normalizedServingPath !== ''
      && scriptUrl.origin === window.location.origin
      && scriptUrl.pathname === normalizedServingPath
    return isGoogleHosted || isSameOriginGateway
  })
}

export const hasGtmScript = (containerId: string, servingPath?: string) =>
  findLoadedTagScript(containerId, '/gtm.js', servingPath) !== undefined

/**
 * Check if gtag script is already loaded
 */
export const hasGtagScript = (measurementId: string, servingPath?: string) =>
  findLoadedTagScript(measurementId, '/gtag/js', servingPath) !== undefined

export const sortEventQueue = (
  a: BaseAnalyticsEvent,
  b: BaseAnalyticsEvent,
): number => {
  // 1. 'init' always comes first
  if (a.eventType === 'init' && b.eventType !== 'init') {
    return -1
  }
  if (a.eventType !== 'init' && b.eventType === 'init') {
    return 1
  }

  // 2. 'set' beats everything EXCEPT 'init'
  if (a.eventType === 'set' && b.eventType !== 'set' && b.eventType !== 'init') {
    return -1
  }
  if (a.eventType !== 'set' && b.eventType === 'set' && a.eventType !== 'init') {
    return 1
  }

  // 3. keep original order for the other events
  return 0
}

const SESSION_STORAGE_KEY = 'dni-analytics-event-queue'
export const addToSessionStorageEventQueue = (customEvent: AnalyticsEvents) => {
  try {
    const existingQueue = getParsedStorageItem<AnalyticsEvents[]>(SESSION_STORAGE_KEY, STORAGE_EVENT.sessionStorage) || []
    const mergedQueue = [...existingQueue, customEvent]
    setStorageItem(SESSION_STORAGE_KEY, mergedQueue, STORAGE_EVENT.sessionStorage)
  }
  catch {
    console.error('[GA4] addToSessionStorageEventQueue failed')
  }
}

export const getSessionStorageEventQueue = (): AnalyticsEvents[] => {
  try {
    const existingQueue = getParsedStorageItem<AnalyticsEvents[]>(SESSION_STORAGE_KEY, STORAGE_EVENT.sessionStorage) || []
    return existingQueue
  }
  catch {
    return []
  }
}

export const clearSessionStorageEventQueue = () => {
  setStorageItem(SESSION_STORAGE_KEY, [], STORAGE_EVENT.sessionStorage)
}

export const OMNI_SEGMENT_TRACKING_PARAMS_KEY = 'omniSegmentTrackingParams'
/**
 * 獲取需要附加到 URL 的查詢參數
 * 只有當當前 URL 不包含存儲中的參數時才返回參數字符串
 */
export const getTrackingQueryParams = (location: Location): string | null => {
  const storedTrackingParams = getParsedStorageItem<{
    paramsString: string
    paramsObject: Record<string, string>
  }>(OMNI_SEGMENT_TRACKING_PARAMS_KEY, STORAGE_EVENT.sessionStorage)

  if (!storedTrackingParams) return null

  const currentUrlParams = new URLSearchParams(location.search)
  const { paramsObject, paramsString } = storedTrackingParams

  const hasMissingTrackingParams = Object.keys(paramsObject).some(
    paramKey => !currentUrlParams.has(paramKey),
  )

  return hasMissingTrackingParams ? paramsString : null
}

export interface AletheiaUtm {
  source?: string
  medium?: string
  campaign?: string
  content?: string
  term?: string
  id?: string
}

const ALETHEIA_SESSION_START_KEY = 'aletheia-session-start'
/**
 * 是否為當前 GA4 session 的首筆 page_view：session_id 與上次記錄不同即回 true 並更新記錄。
 * 對齊 GA4 session 邊界（30 分閒置／午夜輪替），供 aletheia 端分辨原始 UTM 觀測與 sbjs 副本。
 */
export const consumeAletheiaSessionStart = (sessionId: string | undefined): boolean => {
  if (!sessionId) return false
  const lastSessionId = getParsedStorageItem<string>(ALETHEIA_SESSION_START_KEY, STORAGE_EVENT.sessionStorage)
  if (lastSessionId === sessionId) return false
  setStorageItem(ALETHEIA_SESSION_START_KEY, sessionId, STORAGE_EVENT.sessionStorage)
  return true
}

export interface AletheiaEventPayload {
  event: {
    app_env: string
    event_name: string
    user_id?: string
    project_code?: string
    country_code?: string
    campaign_code?: string
    ga_client_id?: string
    utm?: AletheiaUtm
    event_info: AnalyticsEvents['eventParams'] & {
      session_id?: string
      document_title: string
      document_location: string
      is_session_start?: boolean
    }
    fbc?: string
    fbp?: string
    event_id?: string
  }
}

/** window.sbjs 由 WooCommerce 內建 sourcebuster 注入；dni-analysis 不依賴 wp-types，於此自帶所需型別 */
interface SbjsVisitorSource {
  typ?: string
  src?: string
  mdm?: string
  cmp?: string
  cnt?: string
  trm?: string
  id?: string
}

export interface SbjsGlobal {
  get?: {
    first?: SbjsVisitorSource
  }
}

// sbjs 以字串 '(none)' 表示無值
const SBJS_NONE_VALUE = '(none)'

// 僅 typ='utm' 的流量帶真正的 UTM 參數；typein/referral/organic 的 src/mdm 是流量來源（如 '(direct)'、referrer 網域），非 UTM
const SBJS_UTM_TRAFFIC_TYPE = 'utm'

const normalizeSbjsValue = (value: string | undefined): string | undefined =>
  value === undefined || value === '' || value === SBJS_NONE_VALUE ? undefined : value

export const buildAletheiaUtm = (source: SbjsVisitorSource | undefined): AletheiaUtm | undefined => {
  if (!source || source.typ !== SBJS_UTM_TRAFFIC_TYPE) return undefined

  const utm: AletheiaUtm = {}
  const assignUtmField = (value: string | undefined, key: keyof AletheiaUtm) => {
    const normalized = normalizeSbjsValue(value)
    if (normalized !== undefined) utm[key] = normalized
  }

  assignUtmField(source.src, 'source')
  assignUtmField(source.mdm, 'medium')
  assignUtmField(source.cmp, 'campaign')
  assignUtmField(source.cnt, 'content')
  assignUtmField(source.trm, 'term')
  assignUtmField(source.id, 'id')

  return Object.keys(utm).length > 0 ? utm : undefined
}

export const sendAletheiaEvent = async (appEnv: string, eventName: string, payload: AletheiaEventPayload) => {
  const endpoint = aletheiaEndpointConfigs.find(config => config.set.has(eventName))?.endpoint
  if (endpoint === undefined) return
  try {
    const apiUrl = `https://aletheia${appEnv === 'production' ? '' : '-beta'}.shepherdtech.group/api/events`
    await axios.post(
      `${apiUrl}${endpoint}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      })
  }
  catch (error) {
    console.error('[Aletheia] sendAnalyticsEvent failed - Network error:', error)
  }
}

export const getMetaPixelClickID = () => {
  const fbc = Cookies.get('_fbc')
  const fbp = Cookies.get('_fbp')
  return {
    fbc,
    fbp,
  }
}

const getUdnFidItem = () => localStorage.getItem('UDN_FID')
export const getUdnFid = () => {
  try {
    const udnFidItem = getUdnFidItem()
    if (!udnFidItem) return { ffid: null, dfid: null }
    const udnFid = safeJsonParse<{ FFID: string, DFID: string }>(udnFidItem)
    if (!udnFid) return { ffid: null, dfid: null }

    return {
      ffid: udnFid.FFID,
      dfid: udnFid.DFID,
    }
  }
  catch (error) {
    console.error('[UDN] Failed to get UDN_FID', error)
    return {
      ffid: null,
      dfid: null,
    }
  }
}

export const loadUdnFingerprintScript = ({ onlyLoadScript = false }: { onlyLoadScript?: boolean } = {}): Promise<void> => {
  const UDN_SCRIPT_SRC = 'https://p.udn.com.tw/upf/static/common/universal-fingerprint.umd.js'
  const isUdnScriptLoaded = () => !!document.querySelector(`script[src="${UDN_SCRIPT_SRC}"]`)
  const isUdnInitialized = () => getUdnFidItem() !== null
  const MAX_RETRY_COUNT = 20
  const RETRY_INTERVAL = 100
  let retryCount = 0

  return new Promise((resolve, reject) => {
    const detectingUdnInitialized = () => {
      if (onlyLoadScript) {
        console.debug('[UDN] onlyLoadScript, skip detectingUdnInitialized')
        resolve()
        return
      }
      console.debug('[UDN] detectingUdnInitialized', isUdnInitialized())
      if (isUdnInitialized()) {
        resolve()
        return
      }
      else {
        retryCount++
        if (retryCount >= MAX_RETRY_COUNT) {
          console.debug('[UDN] UDN_FID is not initialized, skip processing.')
          resolve()
          return
        }
        setTimeout(() => {
          detectingUdnInitialized()
        }, RETRY_INTERVAL)
      }
    }

    if (isUdnScriptLoaded()) {
      detectingUdnInitialized()
    }
    else {
      const script = document.createElement('script')
      script.charset = 'UTF-8'
      script.async = true
      script.crossOrigin = 'anonymous'
      script.src = UDN_SCRIPT_SRC

      script.onload = () => {
        detectingUdnInitialized()
      }

      script.onerror = () => reject(new Error(`Failed to load script: ${UDN_SCRIPT_SRC}`))

      document.head.appendChild(script)
    }
  })
}

/** Omnichat */
const OMNICHAT_PLUGIN_ID = 'omnichat-plugin'

export const insertOmnichatScript = () => {
  const firstScript = document.getElementsByTagName('script')[0]
  const script = document.createElement('script')
  script.async = true
  script.id = OMNICHAT_PLUGIN_ID
  script.setAttribute('src', 'https://chat-plugin.easychat.co/easychat.js')
  firstScript?.parentNode?.insertBefore(script, firstScript)
}

/**
 * Check if Omnichat script is already loaded
 */
export const hasOmnichatScript = () => {
  const existingScript = document.getElementById(OMNICHAT_PLUGIN_ID)
  return !!existingScript
}
